import React, { useEffect, useRef, useState } from "react";
import { LineChart, Line } from "recharts";
import VerifiedNavigation from "../../navigation/components/VerifiedNavigation";
import { AuthServices } from "../../auth/services/AuthServices";
import "../../../styles/dashboard.css";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const navigate = useNavigate();
  const socketRef = useRef();
  const [profile, setProfile] = useState({});
  const [analytics, setAnalytics] = useState({});
  const [orderCountMetrics, setOrderCountMetrics] = useState([]);
  const [avgOrderMetrics, setAvgOrderMetrics] = useState([]);
  const [revenueMetrics, setRevenueMetrics] = useState([]);
  const [showSetupPrompt, setShowSetupPrompt] = useState(false);

  const fetchProfile = async () => {
    try {
      const response = await AuthServices.fetchProfile();

      const businessMetrics = response.data.analytics.history;
      const avgOrderMetrics = businessMetrics.map((doc) => ({
        date: doc.date,
        value: doc.metrics.avgOrderValue,
      }));
      const orderCountMetrics = businessMetrics.map((doc) => ({
        date: doc.date,
        value: doc.metrics.ordersCount,
      }));
      const revenueMetrics = businessMetrics.map((doc) => ({
        date: doc.date,
        value: doc.metrics.revenue,
      }));

      setOrderCountMetrics(orderCountMetrics);
      setRevenueMetrics(revenueMetrics);
      setAvgOrderMetrics(avgOrderMetrics);
      setProfile(response.data?.user);
      setAnalytics(response.data?.analytics);
    } catch (error) {
      console.error("There was an error with fetching the profile");
    }
  };

  useEffect(() => {
    socketRef.current = io(process.env.REACT_APP_WEBSOCKET_URL, {
      transports: ["websocket"],
      withCredentials: true,
    });

    socketRef.current.on("connect", () => {
      console.log("✅ Connected to socket:", socketRef.current.id);
    });

    return () => {
      socketRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile?._id && socketRef.current?.connected) {
      const userId = profile._id;
      socketRef.current.emit("joinRoom", {
        userId,
        businessSocket: socketRef.current.id,
      });
      console.log(
        `📡 Joined room ${socketRef.current.id} for business:`,
        userId
      );
    }
  }, [profile]);

  useEffect(() => {
    const profileIncomplete = profile.iconPhoto === "";

    if (profileIncomplete) {
      setShowSetupPrompt(true);
    }
  }, [profile]);

  return (
    <div className="dashboard">
      <VerifiedNavigation />
      <div className="dashboard-container">
        {showSetupPrompt && (
          <div className="setup-modal-overlay">
            <div className="setup-modal">
              <h2>Finish setting up your profile</h2>
              <p>
                To be visible on the customer app, please add your Business Logo
                inside the settings.
              </p>
              <button
                className="setup-modal-button"
                onClick={() => navigate("/manager/settings")}
              >
                Go to Settings
              </button>
              <button
                className="setup-modal-secondary"
                onClick={() => setShowSetupPrompt(false)}
              >
                I’ll do this later
              </button>
            </div>
          </div>
        )}

        <header className="dashboard-header">
          <div className="profile">
            <div>
              {" "}
              <img
                className="avatar"
                src={profile?.iconPhoto || profile?.businessName}
                alt={profile?.businessName}
              />
            </div>
            <div>
              <h2>Welcome, {profile?.businessName}</h2>
              <p>{new Date().toLocaleDateString()}</p>
            </div>
          </div>
          <div className="header-actions">
            {/* <input type="text" placeholder="Search" /> */}
            {/* <div className="date-range">Jan 10, 2025 - Jan 16, 2025</div> */}
          </div>
        </header>
        <div className="dashboard-container-main">
          <div className="dashboard-container-left">
            <div className="stats-row">
              <StatCard
                title="Sales"
                value="0"
                // change="+4%"
                data={revenueMetrics}
              />
              <StatCard
                title="Orders"
                value={analytics?.metrics?.ordersCount}
                // change="+500%"
                data={orderCountMetrics}
              />
              <StatCard
                title="Average order value"
                value={analytics?.metrics?.avgOrderValue}
                // change="+6.0%"
                data={avgOrderMetrics}
              />
            </div>

            <section className="overview">
              <div className="overview-header">
                <h3>${analytics?.metrics?.revenue}</h3>
                {/* <span className="positive">+3.2% vs last 30 days</span> */}
                <div className="filters">
                  {/* <button>12 months</button>
                  <button>30 days</button> */}
                  <button>7 days</button>
                  <button>24 hours</button>
                </div>
              </div>

              <div style={{ width: "600px", height: "200px" }}>
                <LineChart width={600} height={200} data={revenueMetrics}>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF6600"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </div>
            </section>
          </div>
          <div className="dashboard-container-right">
            <aside className="sidebar">
              <OpportunityCard
                title="Get $100 free marketing credits to try a sponsored listing ad"
                description="All of your stores are eligible for this free trial. It’s a risk-free way to boost visibility..."
              />
              <OpportunityCard
                title="Respond to last week’s customer reviews before it’s too late"
                description="Last week, you received 35 new reviews. You can send a thank-you note..."
              />
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, change, data }) {
  return (
    <div className="stat-card">
      <h4>{title}</h4>
      <h2>{value}</h2>
      <p className="positive">{change}</p>

      <div style={{ width: 150, height: 50 }}>
        <LineChart width={150} height={50} data={data}>
          <Line
            type="monotone"
            dataKey="value"
            stroke="#FF6600"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </div>
    </div>
  );
}

function OpportunityCard({ title, description }) {
  return (
    <div className="opportunity-card">
      <h5>{title}</h5>
      <p>{description}</p>
      <button>Try it now →</button>
    </div>
  );
}
